import { useState } from "react";
import { useNavigate } from "react-router";
import { Button } from "../../../components/ui/Button";
import { SettingsGroup } from "../../../components/ui/Settings";
import { IconChevronRight, IconLock, IconPlus } from "../../../components/icons";
import { channelIcon } from "../ChannelSidebar";
import { ChannelDialog } from "../ChannelDialogs";
import { permsOf } from "../hooks";
import type { SectionProps } from "./shared";

/* ───────────── List ───────────── */

export function ChannelsSection({ structure }: SectionProps) {
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const perms = permsOf(structure.community.myPermissions, structure.community.isOwner);
  const groups = [
    { id: "none", name: "No category", channels: structure.channels.filter((c) => !c.categoryId) },
    ...structure.categories.map((cat) => ({ id: cat.id, name: cat.name, channels: structure.channels.filter((c) => c.categoryId === cat.id) })),
  ].filter((g) => g.channels.length || g.id !== "none");

  return (
    <div>
      {perms.manageChannels && (
        <div className="mb-4 flex justify-end">
          <Button size="sm" icon={IconPlus} onClick={() => setCreateOpen(true)}>
            Create channel
          </Button>
        </div>
      )}
      {groups.map((g) => (
        <SettingsGroup key={g.id} title={g.name}>
          {g.channels.map((c) => {
            const Icon = channelIcon(c);
            const overrides = c.overwrites?.length ?? 0;
            return (
              <button key={c.id} onClick={() => navigate(`/c/${structure.community.id}/${c.id}/settings`)} className="flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-surface">
                <Icon className="icon size-[18px] shrink-0 text-fg-3" aria-hidden />
                <span className="min-w-0 flex-1 truncate text-sm font-medium">{c.name}</span>
                <span className="flex items-center gap-3 text-xs text-fg-3 tabular">
                  {c.isPrivate && (
                    <span className="flex items-center gap-1">
                      <IconLock className="icon size-3.5" aria-hidden /> Private
                    </span>
                  )}
                  {overrides > 0 && <span>{overrides} {overrides === 1 ? "override" : "overrides"}</span>}
                  {c.slowModeSeconds > 0 && <span>Slow mode</span>}
                  {c.type === "voice" && c.userLimit > 0 && <span>Limit {c.userLimit}</span>}
                  {c.ageRestricted && <span>Age-restricted</span>}
                </span>
                <IconChevronRight className="icon size-4 text-fg-3" aria-hidden />
              </button>
            );
          })}
          {g.channels.length === 0 && <p className="px-4 py-3 text-sm text-fg-3">No channels.</p>}
        </SettingsGroup>
      ))}
      <ChannelDialog open={createOpen} onOpenChange={setCreateOpen} structure={structure} defaultCategoryId={null} />
    </div>
  );
}

